import type { SubsPlanType } from '@/app/types';
import type { PlanRight } from './types';

/**
 * 个人版套餐 plan_type。team_v2 走独立的 TeamPlanCard，不出现在以下常量里。
 */
export type IndividualPlanType = Exclude<SubsPlanType, 'team_v2'>;

export const PLAN_TITLE_TYPE_MAP: Record<IndividualPlanType, React.CSSProperties> = {
  free: {
    color: '#fff',
  },
  standard_v2: {
    backgroundImage: 'linear-gradient(90deg, #CFE7FF 0%, #79A4FF 100%)',
  },
  premium_v2: {
    backgroundImage: 'linear-gradient(91deg, #FAF1C8 3.96%, #BDA16C 96.78%)',
  },
  ultimate_v2: {
    backgroundImage: 'linear-gradient(90deg, #E0DDFF 2.78%, #B494F2 95.83%)',
  },
};

// 积分list
export const CREDITS_CONFIG_LIST: Record<
  IndividualPlanType,
  {
    credits: number | string;
    videos: number | string;
    bgImg: string;
  }
> = {
  standard_v2: {
    credits: 800,
    videos: 200,
    bgImg: 'bg-[linear-gradient(270deg,#78A3FF,#D0E8FF)]',
  },
  premium_v2: {
    credits: 4000,
    videos: 1000,
    bgImg: 'bg-[linear-gradient(270deg,#BA9D64,#FFF7D0)]',
  },
  ultimate_v2: {
    credits: 8000,
    videos: 2000,
    bgImg: 'bg-[linear-gradient(270deg,#B1A7FF,#D6D0FF)]',
  },
  free: {
    credits: 0,
    videos: 0,
    bgImg: '',
  },
};

/**
 * 试用期积分与视频数量配置：试用 tab 下各档位的积分/视频上限。
 * - Standard: 150 积分 ≈ 38 视频
 * - Premium: 500 积分 ≈ 125 视频
 * - Ultimate: 1000 积分 ≈ 250 视频
 */
export const TRIAL_CREDITS_CONFIG: Record<
  'standard_v2' | 'premium_v2' | 'ultimate_v2',
  { credits: number; videos: number }
> = {
  standard_v2: { credits: 150, videos: 38 },
  premium_v2: { credits: 500, videos: 125 },
  ultimate_v2: { credits: 1000, videos: 250 },
};

export const MAX_PLAN_VIDEOS = Math.max(
  ...Object.entries(CREDITS_CONFIG_LIST)
    .filter(([key]) => key !== 'free')
    .map(([, v]) => (typeof v.videos === 'number' ? v.videos : 0)),
);

/**
 * 各档"核心权益"列表（紫色 icon + UNLIMITED 风格）。
 */
export const PLAN_HIGHLIGHTED_RIGHTS: Record<IndividualPlanType, readonly PlanRight[]> = {
  free: [],
  standard_v2: ['fastSpeedRight', 'creditsPurchase', 'agentBasicRight', 'referenceRight'],
  premium_v2: ['highSpeedRight', 'creditsPurchase', 'agentMoreRight', 'referenceRight', 'newFeature'],
  ultimate_v2: [
    'ultraFastSpeedRight',
    'q2ImageUnlimited',
    'creditsPurchase',
    'agentRight',
    'newFeature',
    'referenceRight',
  ],
};

/** 各档"普通权益"列表（灰色 check 风格）。 */
export const PLAN_COMMON_RIGHTS: Record<IndividualPlanType, readonly PlanRight[]> = {
  free: ['referenceFreeRight'],
  standard_v2: ['durationRight', 'qualityRight', 'modeRight', 'proMode', 'encodingFormatRight', 'commercialUse'],
  premium_v2: ['durationRight', 'qualityRight', 'modeRight', 'proMode', 'encodingFormatRight', 'commercialUse'],
  ultimate_v2: ['durationRight', 'qualityRight', 'modeRight', 'proMode', 'encodingFormatRight', 'commercialUse'],
};

/** 国内站达人合作码文案（仅 SITE=china 使用，无需走 i18n） */
export const INFLUENCER_CODE_TEXT = {
  dialog: {
    error: '提交失败，请检查合作码是否正确',
    placeholder: '请填写达人合作码',
    submit: '提交',
    success: '提交成功，下单支持你心仪的达人吧～',
    title: '达人合作码',
  },
  entry: {
    coopCode: '达人合作码',
  },
  paymentError: '达人合作码错误/失效',
} as const;
